import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WppButton, WppTag } from '@wppopen/components-library-react'
import TaskMetadataHeader from 'components/TaskMetadataHeader/TaskMetadataHeader'
import RenderNavButtons from 'components/RenderNavButtons/RenderNavButtons'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import Tabs from 'components/Tabs/Tabs'
import { unknown } from 'utils/constants'
import { getFormattedDate } from 'utils'
import { statusChecker } from 'components/StatusMessage/Status'
import { DEFAULT_TAB, tabsData } from '../utils'
import type { v5 } from '@contracts'
import type { Actions, TabsAndKeywords } from './PdpHeader-types.d'
import type { HeaderData } from 'components/TaskMetadataHeader/TaskMetadataHeader.d'
import type { TabsObject, pdpTabIds } from 'components/Tabs/tabs-type.d'
import styles from './PdpHeader.module.scss'
import PageHeaderStatusRow from 'components/PageHeaderStatusRow/PageHeaderStatusRow'
import AsyncTypography from 'components/AsyncTypography/AsyncTypography'
import usePermissions from 'hooks/usePermissions'

interface PdpHeaderProps {
  jobData: v5.JobsDetailData | null
  headerData: HeaderData[]
  actions?: Actions
  tabsAndKeywords?: TabsAndKeywords
  isLoading?: boolean
}

export default function PdpHeader({ jobData, headerData, actions, tabsAndKeywords, isLoading }: PdpHeaderProps) {
  const { contentTypeBeingEdited, setContentTypeBeingEdited, setIsPageEditable, setIsResubmitModalOpen } =
    useContext(ViewTaskPageContext)
  const [tabs, setTabs] = useState(tabsData)
  const navigate = useNavigate()
  const isApproved = ['completed'].includes(jobData?.status || '')

  const { isGetPermissionsLoading } = usePermissions({})

  const isInitialising = isLoading || !jobData || isGetPermissionsLoading

  // if ActiveTab is not default reset editing functionality to false
  useEffect(() => {
    if (!tabsAndKeywords) return
    if (tabsAndKeywords.activeTab !== DEFAULT_TAB) {
      setIsPageEditable(false)
      setIsResubmitModalOpen(false)
      setContentTypeBeingEdited(null)
    }
  }, [tabsAndKeywords?.activeTab])

  // add new tab ('Parent standard content' or 'Parent retailer adaption') for different job type if tabs don't have it already
  useEffect(() => {
    const typeRetailer = jobData?.type === 'retailer_adaption' && jobData?.config?.standard_content_job_id
    const typeSku = jobData?.type === 'sku_adaption' && jobData?.config?.retailer_adaption_job_id
    const typeVariant = !!(jobData?.config as any)?.variant

    setTabs(prevState => {
      let newTabs: TabsObject[] = [...prevState]

      if (typeRetailer || typeSku) {
        // if tab already exists then return prevState
        if (prevState.some(tab => tab.view === 'parent_content')) return prevState
        // add new tab in 2nd position of the array
        const tabText = typeRetailer ? 'Parent standard content' : 'Parent retailer adaption'
        const newTab = { displayText: tabText, view: 'parent_content' } as TabsObject
        newTabs = [prevState[0], newTab, ...prevState.slice(1)]
      } else {
        newTabs = newTabs.filter(tab => tab.view !== 'parent_content')
      }

      if (typeVariant) {
        // if tab already exists then return prevState
        if (newTabs.some(tab => tab.view === 'variant_data')) return newTabs

        // Find the index of the "Product data" tab
        const productDataIndex = newTabs.findIndex(tab => tab.view === 'product_data')

        // Insert the "Variant data" tab after the "Product data" tab
        const newTab: TabsObject = { displayText: 'Variant data', view: 'variant_data' }
        newTabs = [...newTabs.slice(0, productDataIndex + 1), newTab, ...newTabs.slice(productDataIndex + 1)]
      } else {
        newTabs = newTabs.filter(tab => tab.view !== 'variant_data')
      }

      if (jobData?.type === 'standard_content') {
        newTabs = newTabs.filter(tab => tab.view !== 'retailer_rules')
      }

      return newTabs
    })
  }, [jobData])

  return (
    <>
      <PageHeaderStatusRow
        isLoading={isInitialising}
        tags={[
          statusChecker(jobData?.status).tag,
          jobData?.uploaded && <WppTag data-testid="uploaded-tag" label="Uploaded" variant="neutral" />,
        ]}
        text={
          isApproved
            ? `Approved: ${getFormattedDate(jobData?.approved_at || '')} by ${jobData?.approved_by || unknown}`
            : `Created: ${getFormattedDate(jobData?.created_at)}`
        }
      />

      {/* title and action section */}
      <div className={styles.pageHeading__container}>
        <AsyncTypography
          type="2xl-heading"
          data-testid="product-name"
          tag="h2"
          isLoading={isInitialising}
          dataTestId="product-name"
        >
          {jobData?.reference_name}
        </AsyncTypography>
        <div className={styles.actions}>
          {actions ? (
            <RenderNavButtons
              status={jobData?.status}
              activeTab={tabsAndKeywords?.activeTab || DEFAULT_TAB}
              onDownloadClick={actions.onDownloadClick}
              isDownloading={actions.isDownloading}
              onApproveClick={actions.onApproveClick}
              isCurrentlyEditing={!!contentTypeBeingEdited}
              isInitialising={isInitialising}
              isLoading={isLoading}
              clientId={jobData?.client_id}
            />
          ) : (
            <WppButton onClick={() => navigate(`/task/${jobData?._id}`)} variant="secondary" data-testid="view-button">
              View
            </WppButton>
          )}
        </div>
      </div>

      {/* Meta data header */}
      <TaskMetadataHeader headerData={headerData} isInitialising={isInitialising} />

      {/* tabs section */}
      {tabsAndKeywords && (
        <Tabs
          currentTab={tabsAndKeywords.activeTab}
          dataKeys={tabs}
          className={styles.pageHeading__tabs}
          align="left"
          onSetNewTab={value => tabsAndKeywords.onActiveTabChange(value as pdpTabIds)}
          isInitialising={isInitialising}
        />
      )}
    </>
  )
}
